import { useMemo, useState, type JSX } from 'react'
import { useWhatsAppTemplates, type WhatsAppTemplate } from '../../hooks/useWhatsAppChats.js'
import { FileText, X } from 'lucide-react'

interface Props {
  onClose: () => void
  onSend: (payload: { name: string; languageCode: string; variables: string[] }) => Promise<void>
}

/** Substitutes {{1}}, {{2}}… so the agent sees the real message before sending. */
function renderPreview(body: string, variables: string[]): string {
  return body.replace(/\{\{(\d+)\}\}/g, (match, index: string) => {
    const value = variables[Number(index) - 1]
    return value?.trim() ? value : match
  })
}

export function TemplatePickerDialog({ onClose, onSend }: Props): JSX.Element {
  const { templates, loading } = useWhatsAppTemplates()
  const [selected, setSelected] = useState<WhatsAppTemplate | null>(null)
  const [variables, setVariables] = useState<string[]>([])
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const preview = useMemo(
    () => (selected ? renderPreview(selected.body_text, variables) : ''),
    [selected, variables],
  )

  function pick(template: WhatsAppTemplate): void {
    setSelected(template)
    setVariables(Array.from({ length: template.variable_count }, () => ''))
    setError(null)
  }

  async function handleSend(): Promise<void> {
    if (!selected || sending) return
    if (variables.some((v) => !v.trim())) {
      setError('Fill in every placeholder before sending.')
      return
    }
    setSending(true)
    setError(null)
    try {
      await onSend({
        name: selected.meta_template_name,
        languageCode: selected.language_code,
        variables,
      })
    } catch {
      setError('WhatsApp rejected the template. Check it is approved for this number.')
    } finally {
      setSending(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="flex max-h-[80vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl bg-white shadow-xl">
        <div className="flex items-center gap-2 border-b border-gray-200 px-5 py-3">
          <FileText className="h-4 w-4 text-[#128C7E]" />
          <h3 className="flex-1 text-sm font-semibold text-gray-800">Send an approved template</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex min-h-0 flex-1">
          <div className="w-1/2 overflow-y-auto border-r border-gray-100">
            {loading ? (
              <div className="p-6 text-center text-sm text-gray-400">Loading templates…</div>
            ) : templates.length === 0 ? (
              <div className="p-6 text-center text-sm text-gray-400">
                No approved templates. Sync them under Admin → Templates first.
              </div>
            ) : (
              templates.map((t) => (
                <button
                  key={t.id}
                  onClick={() => pick(t)}
                  className={`w-full border-b border-gray-100 px-4 py-3 text-left transition-colors ${
                    selected?.id === t.id ? 'bg-[#25D366]/10' : 'hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="truncate text-xs font-semibold text-gray-800">
                      {t.meta_template_name}
                    </span>
                    <span className="flex-shrink-0 rounded bg-gray-100 px-1.5 py-0.5 text-[10px] uppercase text-gray-500">
                      {t.language_code}
                    </span>
                  </div>
                  <p className="mt-1 line-clamp-2 text-[11px] text-gray-500">{t.body_text}</p>
                </button>
              ))
            )}
          </div>

          <div className="flex w-1/2 flex-col overflow-y-auto p-4">
            {!selected ? (
              <p className="m-auto text-xs text-gray-400">Pick a template to fill in.</p>
            ) : (
              <>
                {variables.length > 0 && (
                  <div className="mb-3 space-y-2">
                    {variables.map((value, index) => (
                      <div key={index}>
                        <label className="mb-1 block text-[11px] font-medium text-gray-500">
                          {`Placeholder {{${index + 1}}}`}
                        </label>
                        <input
                          type="text"
                          value={value}
                          onChange={(e) =>
                            setVariables((prev) => prev.map((v, i) => (i === index ? e.target.value : v)))
                          }
                          className="w-full rounded-md border border-gray-200 px-2.5 py-1.5 text-xs outline-none focus:border-[#25D366] focus:ring-1 focus:ring-[#25D366]/20"
                        />
                      </div>
                    ))}
                  </div>
                )}
                <div className="rounded-lg bg-[#D9FDD3] p-3 text-xs text-gray-800">
                  <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-[#128C7E]">
                    Preview
                  </p>
                  <p className="whitespace-pre-wrap">{preview}</p>
                  {selected.footer_content && (
                    <p className="mt-2 text-[10px] text-gray-500">{selected.footer_content}</p>
                  )}
                </div>
              </>
            )}
            {error && <p className="mt-3 text-[11px] text-red-600">{error}</p>}
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-gray-200 px-5 py-3">
          <button
            onClick={onClose}
            className="rounded-md border border-gray-200 px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={() => void handleSend()}
            disabled={!selected || sending}
            className="rounded-md bg-[#25D366] px-4 py-1.5 text-xs font-medium text-white transition-colors hover:bg-[#128C7E] disabled:opacity-40"
          >
            {sending ? 'Sending…' : 'Send template'}
          </button>
        </div>
      </div>
    </div>
  )
}
